import { Menu, MenuItem, Stack, Typography } from '@mui/material';
import React from 'react';
import { PopoverProps } from 'src/components/PopoverTrigger';
import { Iconify } from 'src/components/iconify';
import { useLocales } from 'src/locales';
import { VoiceChannelMember } from 'src/provider/VoiceChannel';
import { useSocketIO } from 'src/socket';
import { VoiceParticipantVolumeControl } from './VoiceParticipantVolumeControl';

export const VoiceParticipantsPopover = ({
  handleClose,
  channelId,
  member,
  isAdmin,
  target,
}: PopoverProps<{
  channelId: string;
  member: VoiceChannelMember;
  isAdmin: boolean;
}>) => {
  const { io } = useSocketIO();
  const { translate } = useLocales();

  const handleMute = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    io.emit('/voice-channel/mute', {
      channelId,
      memberId: member.user._id,
    });
    handleClose();
  };

  const handleKick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    io.emit('/voice-channel/kick', {
      channelId,
      memberId: member.user._id,
    });
    handleClose();
  };

  const menuOptions = [
    {
      title: translate('communicate.voice_channel.mute_participant'),
      icon: 'tabler:microphone-off',
      action: (event: React.MouseEvent<HTMLElement>) => handleMute(event),
    },
    {
      title: translate('communicate.voice_channel.kick_participant'),
      icon: 'tabler:phone-off',
      action: (event: React.MouseEvent<HTMLElement>) => handleKick(event),
    },
  ];

  return (
    <Menu
      open
      anchorEl={target}
      onClose={(e) => {
        // stop click event from bubbling up to the parent and trigger an navigation
        if ('preventDefault' in e && typeof e.preventDefault === 'function')
          e.preventDefault();
        if ('stopPropagation' in e && typeof e.stopPropagation === 'function')
          e.stopPropagation();
        handleClose();
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      disableRestoreFocus
    >
      <Stack
        px={2}
        sx={(theme) => ({
          ...(isAdmin && {
            paddingBottom: theme.spacing(0.5),
            marginBottom: theme.spacing(1.5),
            borderBottom: `1px solid ${theme.palette.divider}`,
          }),
        })}
      >
        <Typography variant="subtitle2" mb={0.5}>
          {translate('communicate.voice_channel.member_local_volume')}
        </Typography>
        <VoiceParticipantVolumeControl meetId={member.meetId} />
      </Stack>
      {isAdmin &&
        menuOptions.map((menu) => (
          <MenuItem key={menu.title} onClick={menu.action}>
            <Iconify icon={menu.icon} color="text.secondary" mr={1} />
            <Typography variant="caption">{menu.title}</Typography>
          </MenuItem>
        ))}
    </Menu>
  );
};
